import { tryOnMounted, tryOnUnmounted } from '@vueuse/core';

import {
  cancelIdleExecution,
  executeWhenIdle,
} from '../functions/idleExecution';
import type { IdleExecutionIdentifier } from '../functions/idleExecution';

type ComponentAsyncImport = () => Promise<unknown>;

interface ComponentPreloadState {
  asyncImport: ComponentAsyncImport;
  preloadId: IdleExecutionIdentifier | null;
  state: ComponentPreloadStatus;
}

type ComponentPreloadStatus = 'idle' | 'loading' | 'loaded';

interface BackgroundComponentsPreloadLifecycleActions {
  start: () => void;
  stop: () => void;
}

/**
 * Manages the preloading of components asynchronously in the background using the browser's idle time.
 *
 * @param preloadComponentAsyncImports - An array of functions that import components asynchronously in sequential order.
 *
 * The function automatically begins preloading components when mounted and cancels
 * any pending preloads when unmounted to prevent memory leaks and unnecessary resource usage.
 */
export function useBackgroundComponentsPreload(
  preloadComponentAsyncImports: ComponentAsyncImport[]
): BackgroundComponentsPreloadLifecycleActions {
  const componentsPreloadState: ComponentPreloadState[] = Array.from(
    preloadComponentAsyncImports
  ).map((asyncImport) => ({
    asyncImport,
    preloadId: null,
    state: globalPreloadedComponents.has(asyncImport) ? 'loaded' : 'idle',
  }));

  let hasStartedOnMounted = false;
  let hasStoppedOnUnmounted = false;

  function preloadComponents(): void {
    const firstIdleComponent = componentsPreloadState.find(
      (component) => component.state === 'idle'
    );

    if (firstIdleComponent) {
      firstIdleComponent.state = 'loading';
      firstIdleComponent.preloadId = executeWhenIdle(async () => {
        await firstIdleComponent.asyncImport();
        firstIdleComponent.state = 'loaded';
        globalPreloadedComponents.add(firstIdleComponent.asyncImport);
        preloadComponents();
      });
    }
  }

  function cancelPendingComponentPreload(): void {
    componentsPreloadState.forEach((component) => {
      if (component.preloadId && component.state !== 'loaded') {
        cancelIdleExecution(component.preloadId);
      }
    });
  }

  tryOnMounted(() => {
    hasStartedOnMounted = true;
    preloadComponents();
  });

  tryOnUnmounted(() => {
    hasStoppedOnUnmounted = true;
    cancelPendingComponentPreload();
  });

  return {
    start: () => !hasStartedOnMounted && preloadComponents,
    stop: () => !hasStoppedOnUnmounted && cancelPendingComponentPreload,
  };
}

const globalPreloadedComponents = new Set<ComponentAsyncImport>();
